import {
  selectOrganizationId,
  useGetCurrentUserQuery,
  useGetDemoQuery,
  useGetDraftsQuery,
  useGetOrganizationQuery,
  useGetWorkspacesQuery,
  useLazyGetModificationsQuery,
} from '@carboncopyco/data';
import { EditorSdkProvider } from '@carboncopyco/editor-sdk';
import {
  CloseOutlineIcon,
  DeleteIcon,
  MouseCursorIcon,
} from '@carboncopyco/icons';
import { RecordingTypes, type TourEngineFrame } from '@carboncopyco/types';
import { ColorHex, ConfirmAlert, TextCaption } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useTourDispatch, useTourSelector } from '../../hooks/store';
import { usePageEvent } from '../../hooks/usePageEvent';
import { useQuery } from '../../hooks/useQuery';
import PageLayout from '../../layouts/PageLayout';
import {
  resetTourCreator,
  selectIsCapturing,
  selectTourFrames,
  setIsCapturing,
  setIsCapturingFinished,
} from '../../store';
import { CAPTURE_IFRAME_ID } from '../../utils/constants';
import { DimensionsContextProvider } from './DimensionsContext';
import { FinishCapturingActionsProvider } from './FinishCapturingActionsContext';
import StartTourCaptureModal from './StartTourCaptureModal';
import TourCaptureDynamicRecorder from './TourCaptureDynamicRecorder';
import TourCaptureDynamicRecorderFromUrl from './TourCaptureDynamicRecorderFromUrl';
import { TourCaptureNavbar } from './TourCaptureNavbar';
import {
  type TourCapturePageProps,
  TourCapturePageProvider,
} from './TourCapturePageContext';
import { TourCaptureSidebar } from './TourCaptureSidebar';
import { useEditorCommunicationLayer } from './useEditorCommunicationLayer';

export const TourCapturePage = (
  props: TourCapturePageProps & {
    webViewRef?: React.RefObject<HTMLElement> | null;
  }
) => {
  const organizationId = useSelector(selectOrganizationId);
  const { navigateToWorkspace } = props.useNavigate();
  const [openDiscardModal, setOpenDiscardModal] = useBoolean();
  const [isFirstFramePopupClosed, setIsFirstFramePopupClosed] = useState(false);
  const [isOpenStartTourCaptureModal, seIsOpenStartTourCaptureModal] =
    useBoolean(false);
  const { demoId, workspaceId } = useParams<{
    demoId: string;
    workspaceId: string;
  }>();

  const isCapturing = useTourSelector(selectIsCapturing(props.uuid));
  const frames = useTourSelector(
    selectTourFrames(props.uuid)
  ) as TourEngineFrame[];

  const isFirstFrameCaptured = frames?.length > 0;

  const dispatch = useTourDispatch();
  const { data: demo } = useGetDemoQuery(
    {
      organizationId,
      workspaceId,
      id: demoId,
    },
    { skip: !demoId }
  );
  const { data: organization } = useGetOrganizationQuery(
    { id: organizationId },
    { skip: !organizationId }
  );

  const { data: userData } = useGetCurrentUserQuery();
  const { data: drafts } = useGetDraftsQuery(
    {
      organizationId,
      sourceType: 'demo',
      sourceId: Number(demoId),
    },
    { skip: !demoId }
  );
  const draft = drafts?.length && drafts[0];

  const [fetchModifications, { data: modifications }] =
    useLazyGetModificationsQuery();

  const editorSdkId = useRef(uuid());
  const { data: workspaces = [] } = useGetWorkspacesQuery({
    organizationId,
  });
  const [_pageTitle, setPageTitle] = useState<string>();
  const containerRef = useRef<HTMLDivElement>(null);
  usePageEvent();

  useEffect(() => {
    dispatch(resetTourCreator({ uuid: props.uuid }));
    return () => {
      dispatch(resetTourCreator({ uuid: props.uuid }));
    };
  }, [dispatch]);

  const query = useQuery();

  const originalTourIdParam = query.get('originalTourId');
  const insertIndexParam = query.get('insertIndex');
  const tourEditorTabId = query.get('tourEditorTabId');

  const originalTourId =
    originalTourIdParam || originalTourIdParam !== ''
      ? originalTourIdParam
      : undefined;
  const insertIndex =
    insertIndexParam || insertIndexParam !== ''
      ? Number.parseInt(insertIndexParam)
      : 0;

  const appUrl = query.get('appUrl');
  const playerToken = query.get('playerToken');
  const fileServiceToken = query.get('fileServiceToken');
  const tourId = query.get('tourId');

  const editorCommunicationLayer = useEditorCommunicationLayer(props.tab?.id);

  usePageEvent();

  const getModifications = () =>
    fetchModifications({
      organizationId,
      entityType: 'draft',
      entityId: String(draft?.id ?? ''),
      apiVersion: 2,
    })
      .unwrap()
      .then((result) => {
        props.onModificationsReady?.(result);
      });

  useEffect(() => {
    if (!draft?.id) {
      return;
    }

    getModifications().catch(console.error);
  }, [draft?.id]);

  const handleOnDiscard = () => {
    if (isCapturing) {
      setOpenDiscardModal.setTrue();
      return;
    }
    navigateToWorkspace(workspaceId);
  };

  const handleConfirmDiscard = () => {
    if (isCapturing) {
      dispatch(
        setIsCapturing({
          uuid: props.uuid,
          isCapturing: false,
        })
      );
      dispatch(
        setIsCapturingFinished({
          uuid: props.uuid,
          isCapturingFinished: false,
        })
      );
      dispatch(resetTourCreator({ uuid: props.uuid }));
      navigateToWorkspace(workspaceId);
    }
  };

  const closeStartTourCaptureModal = () => {
    seIsOpenStartTourCaptureModal.setFalse();
    props.onModalClosed?.();
  };

  const renderClickToCaptureFirstFramePopup = () => (
    <div className="z-100 flex absolute left-1/2 transform -translate-x-1/2 items-center bg-transparent shadow-ds-lg rounded-md w-96 mt-4">
      <div className="flex flex-1 justify-between items-center border border-ds-natural-600 bg-ds-natural-600 rounded-md cursor-pointer-none p-2 px-4">
        <MouseCursorIcon
          size={24}
          color={ColorHex.natural50}
        />
        <TextCaption
          label="Click on the screen to capture the first frame!"
          color="text-ds-accent3-100"
        />
        <div
          onClick={() => setIsFirstFramePopupClosed(true)}
          className="cursor-pointer"
        >
          <CloseOutlineIcon
            size={20}
            color={ColorHex.natural50}
          />
        </div>
      </div>
    </div>
  );

  return (
    <TourCapturePageProvider {...props}>
      <FinishCapturingActionsProvider>
        <DimensionsContextProvider viewElement={containerRef?.current}>
          <EditorSdkProvider
            editorSdkId={editorSdkId.current}
            mode="preview"
            communicationLayer={editorCommunicationLayer}
            recordingType="cloner"
            iframeId={CAPTURE_IFRAME_ID}
            organizationData={organization}
            userData={userData?.user}
            draft={draft}
            modifications={modifications}
            // Used only when making new edits, so it can stay empty
            filesServiceDomain=""
          >
            <PageLayout
              navbar={
                <TourCaptureNavbar
                  entityName={demo?.demoName || 'Untitled Tour'}
                  onDiscard={handleOnDiscard}
                  originalTourId={originalTourId}
                  demoId={demoId}
                  workspaceId={workspaceId}
                  workspaces={workspaces}
                  isFirstFrameCaptured={frames?.length > 0}
                  url={appUrl}
                  isOpenStartTourCaptureModal={isOpenStartTourCaptureModal}
                  containerRef={containerRef}
                />
              }
              sidebar={
                <TourCaptureSidebar
                  frames={frames}
                  originalTourId={originalTourId}
                  demoId={demoId}
                  recordingType={demo?.recordingType || RecordingTypes.overlay}
                  isOpenStartTourCaptureModal={isOpenStartTourCaptureModal}
                />
              }
            >
              <>
                <div
                  className="h-full w-full relative"
                  ref={containerRef}
                >
                  {!props.webViewFrame &&
                    isCapturing &&
                    !isFirstFrameCaptured &&
                    !isFirstFramePopupClosed &&
                    renderClickToCaptureFirstFramePopup()}
                  {!tourId ? (
                    demo && (
                      <>
                        <StartTourCaptureModal
                          anchorRef={containerRef}
                          open={isOpenStartTourCaptureModal}
                          onConfirm={closeStartTourCaptureModal}
                          description={
                            originalTourId
                              ? "Frames captured here will be added to your existing tour. When you're ready, click “Capture new frames”."
                              : 'Start by going to your tour’s first screen. When you’re ready, click “Start capturing”.'
                          }
                        />
                        <TourCaptureDynamicRecorder
                          demo={demo}
                          setPageTitle={setPageTitle}
                          originalTourId={originalTourId}
                          insertIndex={insertIndex}
                          webViewRef={props.webViewRef}
                          onDimensionChange={props.onDimensionChange}
                          tourEditorTabId={Number(tourEditorTabId)}
                        />
                      </>
                    )
                  ) : (
                    <>
                      <StartTourCaptureModal
                        anchorRef={containerRef}
                        open={isOpenStartTourCaptureModal}
                        onConfirm={closeStartTourCaptureModal}
                        description={
                          originalTourId
                            ? "Frames captured here will be added to your existing tour. When you're ready, click “Capture new frames”."
                            : 'Start by going to your tour’s first screen. When you’re ready, click “Start capturing”.'
                        }
                      />
                      <TourCaptureDynamicRecorderFromUrl
                        url={appUrl}
                        playerToken={playerToken}
                        fileServiceToken={fileServiceToken}
                        tourId={Number.parseInt(tourId)}
                        setPageTitle={setPageTitle}
                        originalTourId={originalTourId}
                        insertIndex={insertIndex}
                        onDimensionChange={props.onDimensionChange}
                        webViewRef={props.webViewRef}
                        tourEditorTabId={Number(tourEditorTabId)}
                      />
                    </>
                  )}
                </div>
                <ConfirmAlert
                  containerClass="w-ds-500"
                  show={openDiscardModal}
                  onConfirm={handleConfirmDiscard}
                  onClose={setOpenDiscardModal.setFalse}
                  title="Discard changes"
                  description="Discard your progress and exit the tour creator?"
                  yesLabel="Discard and exit"
                  noLabel="Cancel"
                  danger
                  yesIcon={<DeleteIcon />}
                />
              </>
            </PageLayout>
          </EditorSdkProvider>
        </DimensionsContextProvider>
      </FinishCapturingActionsProvider>
    </TourCapturePageProvider>
  );
};
